import React, { useState } from 'react';
import { Box, Button, ToggleButton, ToggleButtonGroup, Alert } from '@mui/material';
import { ViewList, AccountTree, Add } from '@mui/icons-material';
import RuleCard from './RuleCard';
import RuleEditorDialog from './RuleEditorDialog';
import DecisionTreeView from './DecisionTreeView';
import { sortRulesByPriority, getNextPriority } from '../../../utils/transformations/ruleTransformations';

const VisualRuleBuilder = ({ rules = [], onChange, strategyId, fallbackPersona }) => {
  const [viewMode, setViewMode] = useState('list'); // 'list', 'tree'
  const [editorOpen, setEditorOpen] = useState(false);
  const [currentRule, setCurrentRule] = useState(null);

  const sortedRules = sortRulesByPriority(rules);

  const handleAddRule = () => {
    setCurrentRule(null);
    setEditorOpen(true);
  };

  const handleEditRule = (rule) => {
    setCurrentRule(rule);
    setEditorOpen(true);
  };

  const handleDeleteRule = (rule) => {
    if (!window.confirm(`Delete rule "${rule.name}"?`)) {
      return;
    }

    const updatedRules = rules.filter(r => r.priority !== rule.priority);
    onChange(updatedRules);
  };

  const handleDuplicateRule = (rule) => {
    const newPriority = getNextPriority(rules);
    const duplicatedRule = {
      ...rule,
      priority: newPriority,
      name: `${rule.name} (Copy)`,
      isActive: false
    };

    onChange([...rules, duplicatedRule]);
  };

  const handleToggleActive = (rule, isActive) => {
    const updatedRules = rules.map(r =>
      r.priority === rule.priority ? { ...r, isActive } : r
    );
    onChange(updatedRules);
  };

  const handleSaveRule = (ruleData) => {
    if (currentRule) {
      // Update existing rule
      const updatedRules = rules.map(r =>
        r.priority === currentRule.priority ? ruleData : r
      );
      onChange(updatedRules);
    } else {
      // Add new rule - assign next priority if not set
      const newRule = {
        ...ruleData,
        priority: ruleData.priority || getNextPriority(rules)
      };
      onChange([...rules, newRule]);
    }

    setEditorOpen(false);
    setCurrentRule(null);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(e, newMode) => newMode && setViewMode(newMode)}
          size="small"
        >
          <ToggleButton value="list">
            <ViewList sx={{ mr: 1 }} />
            List View
          </ToggleButton>
          <ToggleButton value="tree">
            <AccountTree sx={{ mr: 1 }} />
            Decision Tree
          </ToggleButton>
        </ToggleButtonGroup>

        {viewMode === 'list' && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAddRule}
          >
            Add Rule
          </Button>
        )}
      </Box>

      {rules.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No rules defined. Add rules to configure how clients are routed to specialized personas.
        </Alert>
      )}

      {viewMode === 'list' && (
        <Box>
          {sortedRules.map((rule) => (
            <RuleCard
              key={rule.priority}
              rule={rule}
              onEdit={handleEditRule}
              onDelete={handleDeleteRule}
              onDuplicate={handleDuplicateRule}
              onTest={() => {}}
              onToggleActive={handleToggleActive}
            />
          ))}
        </Box>
      )}

      {viewMode === 'tree' && (
        <DecisionTreeView
          rules={sortedRules}
          fallbackPersona={fallbackPersona}
        />
      )}

      <RuleEditorDialog
        open={editorOpen}
        rule={currentRule}
        onClose={() => {
          setEditorOpen(false);
          setCurrentRule(null);
        }}
        onSave={handleSaveRule}
      />
    </Box>
  );
};

export default VisualRuleBuilder;
